import { t } from "@/lib/i18n/interpolate";
import {
  type DocBlock,
  type DocModel,
  type DocSection,
  ensurePeriod,
  linesOf,
} from "@/lib/tool-kit/research-workflow/document";

/**
 * Data management plans, laid out to four templates from one set of answers.
 *
 * The researcher answers each question once; a template decides which answers go
 * under which of its headings. The layouts follow the published documents:
 *
 * - **generic** — the eight sections of the DCC *Checklist for a Data Management Plan*
 *   (v4.0, 2013), the structure most institutional templates descend from.
 * - **nsf** — the sections of NSF's Research.gov DMSP webform, which replaced the PDF
 *   upload for proposals from 27 April 2026 (Policy Notice NSF 26-202). The form asks
 *   these questions for each of up to four data or research product categories.
 * - **nsfClassic** — the five elements of the two-page plan in PAPPG 24-1, which many
 *   institutional and foundation templates still copy.
 * - **horizon** — the Horizon Europe DMP template (v1.1, 2022): data summary, the four
 *   FAIR sub-sections, other outputs, resources, security, ethics and other issues.
 *
 * An empty required answer becomes the question itself in brackets, so the draft is
 * also the list of what is left to decide.
 */

export type DmpTemplateId = "generic" | "nsf" | "nsfClassic" | "horizon";

export type DmpTextField =
  | "description"
  | "dataDescription"
  | "reuse"
  | "formats"
  | "volume"
  | "methods"
  | "metadata"
  | "standards"
  | "ethics"
  | "ip"
  | "storage"
  | "security"
  | "preservation"
  | "repository"
  | "sharing"
  | "timeline"
  | "restrictions"
  | "utility"
  | "otherOutputs"
  | "responsibilities"
  | "costs"
  | "otherProcedures";

export type DmpField = DmpTextField | "pid";

export interface DmpInput extends Record<DmpTextField, string> {
  title: string;
  pi: string;
  funder: string;
  grant: string;
  /** Already formatted for the reader's locale, or empty. */
  date: string;
  /** The repository assigns a persistent identifier such as a DOI. */
  doi: boolean;
}

export const EMPTY_DMP_INPUT: DmpInput = {
  title: "",
  pi: "",
  funder: "",
  grant: "",
  date: "",
  doi: false,
  description: "",
  dataDescription: "",
  reuse: "",
  formats: "",
  volume: "",
  methods: "",
  metadata: "",
  standards: "",
  ethics: "",
  ip: "",
  storage: "",
  security: "",
  preservation: "",
  repository: "",
  sharing: "",
  timeline: "",
  restrictions: "",
  utility: "",
  otherOutputs: "",
  responsibilities: "",
  costs: "",
  otherProcedures: "",
};

/** `[field, optional]` — an optional field is left out when empty instead of prompted. */
type FieldRef = readonly [DmpField, boolean];

interface PartSpec {
  /** A sub-heading key, for templates that number their questions. */
  heading?: string;
  fields: readonly FieldRef[];
}

interface SectionSpec {
  heading: string;
  parts: readonly PartSpec[];
}

const REQUIRED = false;
const OPTIONAL = true;

/** Which answers go under which heading, template by template. Keys are heading keys. */
export const DMP_TEMPLATES: Record<DmpTemplateId, readonly SectionSpec[]> = {
  generic: [
    {
      heading: "dccAdmin",
      parts: [
        {
          fields: [
            ["description", REQUIRED],
            ["otherProcedures", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "dccCollection",
      parts: [
        {
          heading: "dccCollectionWhat",
          fields: [
            ["dataDescription", REQUIRED],
            ["reuse", OPTIONAL],
            ["otherOutputs", OPTIONAL],
            ["formats", REQUIRED],
            ["volume", REQUIRED],
          ],
        },
        { heading: "dccCollectionHow", fields: [["methods", REQUIRED]] },
      ],
    },
    {
      heading: "dccDocumentation",
      parts: [
        {
          heading: "dccDocumentationWhat",
          fields: [
            ["metadata", REQUIRED],
            ["standards", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "dccEthics",
      parts: [
        { heading: "dccEthicsIssues", fields: [["ethics", REQUIRED]] },
        { heading: "dccEthicsIp", fields: [["ip", REQUIRED]] },
      ],
    },
    {
      heading: "dccStorage",
      parts: [
        { heading: "dccStorageBackup", fields: [["storage", REQUIRED]] },
        { heading: "dccStorageSecurity", fields: [["security", REQUIRED]] },
      ],
    },
    {
      heading: "dccPreservation",
      parts: [
        {
          heading: "dccPreservationWhich",
          fields: [
            ["preservation", REQUIRED],
            ["utility", OPTIONAL],
          ],
        },
        {
          heading: "dccPreservationPlan",
          fields: [
            ["repository", REQUIRED],
            ["pid", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "dccSharing",
      parts: [
        {
          heading: "dccSharingHow",
          fields: [
            ["sharing", REQUIRED],
            ["timeline", REQUIRED],
          ],
        },
        {
          heading: "dccSharingRestrictions",
          fields: [["restrictions", REQUIRED]],
        },
      ],
    },
    {
      heading: "dccResponsibilities",
      parts: [
        {
          heading: "dccResponsibilitiesWho",
          fields: [["responsibilities", REQUIRED]],
        },
        {
          heading: "dccResponsibilitiesResources",
          fields: [["costs", REQUIRED]],
        },
      ],
    },
  ],
  nsf: [
    {
      heading: "nsfCategories",
      parts: [
        {
          fields: [
            ["dataDescription", REQUIRED],
            ["otherOutputs", OPTIONAL],
            ["formats", REQUIRED],
            ["volume", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "nsfAccess",
      parts: [
        {
          fields: [
            ["sharing", REQUIRED],
            ["restrictions", REQUIRED],
            ["ethics", OPTIONAL],
            ["ip", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "nsfStandards",
      parts: [
        {
          fields: [
            ["metadata", REQUIRED],
            ["standards", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "nsfProvenance",
      parts: [
        {
          fields: [
            ["reuse", OPTIONAL],
            ["methods", REQUIRED],
          ],
        },
      ],
    },
    {
      heading: "nsfArchiving",
      parts: [
        {
          fields: [
            ["repository", REQUIRED],
            ["pid", OPTIONAL],
          ],
        },
      ],
    },
    { heading: "nsfTimeline", parts: [{ fields: [["timeline", REQUIRED]] }] },
    {
      heading: "nsfAvailability",
      parts: [{ fields: [["preservation", REQUIRED]] }],
    },
    {
      heading: "nsfAccountability",
      parts: [{ fields: [["responsibilities", REQUIRED]] }],
    },
  ],
  nsfClassic: [
    {
      heading: "classicTypes",
      parts: [
        {
          fields: [
            ["dataDescription", REQUIRED],
            ["reuse", OPTIONAL],
            ["otherOutputs", OPTIONAL],
            ["formats", REQUIRED],
            ["volume", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "classicStandards",
      parts: [
        {
          fields: [
            ["metadata", REQUIRED],
            ["standards", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "classicAccess",
      parts: [
        {
          fields: [
            ["sharing", REQUIRED],
            ["timeline", REQUIRED],
            ["restrictions", REQUIRED],
            ["ethics", OPTIONAL],
            ["security", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "classicReuse",
      parts: [
        {
          fields: [
            ["ip", REQUIRED],
            ["utility", OPTIONAL],
          ],
        },
      ],
    },
    {
      heading: "classicArchiving",
      parts: [
        {
          fields: [
            ["repository", REQUIRED],
            ["pid", OPTIONAL],
            ["preservation", REQUIRED],
          ],
        },
      ],
    },
  ],
  horizon: [
    {
      heading: "heSummary",
      parts: [
        {
          fields: [
            ["description", REQUIRED],
            ["dataDescription", REQUIRED],
            ["reuse", OPTIONAL],
            ["formats", REQUIRED],
            ["volume", REQUIRED],
            ["utility", REQUIRED],
          ],
        },
      ],
    },
    {
      heading: "heFair",
      parts: [
        {
          heading: "heFindable",
          fields: [
            ["pid", OPTIONAL],
            ["metadata", REQUIRED],
          ],
        },
        {
          heading: "heAccessible",
          fields: [
            ["repository", REQUIRED],
            ["sharing", REQUIRED],
            ["timeline", REQUIRED],
            ["restrictions", REQUIRED],
          ],
        },
        { heading: "heInteroperable", fields: [["standards", REQUIRED]] },
        {
          heading: "heReuse",
          fields: [
            ["ip", REQUIRED],
            ["methods", REQUIRED],
          ],
        },
      ],
    },
    {
      heading: "heOtherOutputs",
      parts: [{ fields: [["otherOutputs", REQUIRED]] }],
    },
    {
      heading: "heResources",
      parts: [
        {
          fields: [
            ["costs", REQUIRED],
            ["responsibilities", REQUIRED],
            ["preservation", REQUIRED],
          ],
        },
      ],
    },
    {
      heading: "heSecurity",
      parts: [
        {
          fields: [
            ["storage", REQUIRED],
            ["security", REQUIRED],
          ],
        },
      ],
    },
    { heading: "heEthics", parts: [{ fields: [["ethics", REQUIRED]] }] },
    {
      heading: "heOther",
      parts: [{ fields: [["otherProcedures", REQUIRED]] }],
    },
  ],
};

/** The plan's words. The widget's strings object satisfies this structurally. */
export interface DmpPhrases {
  docTitle: string;
  templateNames: Readonly<Record<DmpTemplateId, string>>;
  metaTemplate: string;
  metaProject: string;
  metaPi: string;
  metaFunder: string;
  metaGrant: string;
  metaDate: string;
  missingTitle: string;
  headings: Readonly<Record<string, string>>;
  prompts: Readonly<Record<DmpTextField, string>>;
  /** Lead-ins for short answers ("File formats:"); prose answers have none. */
  leads: Readonly<Partial<Record<DmpTextField, string>>>;
  pidSentence: string;
}

function isFilled(input: DmpInput, field: DmpField): boolean {
  return field === "pid" ? input.doi : input[field].trim() !== "";
}

function fieldBlocks(
  input: DmpInput,
  [field, optional]: FieldRef,
  p: DmpPhrases
): DocBlock[] {
  if (field === "pid") {
    return input.doi ? [{ kind: "p", text: p.pidSentence }] : [];
  }
  const lines = linesOf(input[field]);
  if (lines.length === 0) {
    return optional ? [] : [{ kind: "p", text: p.prompts[field] }];
  }
  const lead = p.leads[field];
  return lines.map((line, index) => ({
    kind: "p",
    text:
      lead && index === 0
        ? `${lead} ${ensurePeriod(line)}`
        : ensurePeriod(line),
  }));
}

function sectionBlocks(
  input: DmpInput,
  spec: SectionSpec,
  p: DmpPhrases
): DocBlock[] {
  return spec.parts.flatMap((part) => {
    const body = part.fields.flatMap((ref) => fieldBlocks(input, ref, p));
    if (!part.heading) {
      return body;
    }
    return [{ kind: "h", text: p.headings[part.heading] ?? "" }, ...body];
  });
}

function metaLines(
  input: DmpInput,
  template: DmpTemplateId,
  p: DmpPhrases
): string[] {
  const value = (raw: string) => raw.trim();
  const lines = [
    t(p.metaProject, { value: value(input.title) || p.missingTitle }),
    t(p.metaTemplate, { value: p.templateNames[template] }),
  ];
  const optional: [string, string][] = [
    [p.metaPi, input.pi],
    [p.metaFunder, input.funder],
    [p.metaGrant, input.grant],
    [p.metaDate, input.date],
  ];
  for (const [template_, raw] of optional) {
    if (value(raw)) {
      lines.push(t(template_, { value: value(raw) }));
    }
  }
  return lines;
}

/** The plan, laid out to `template`. */
export function buildDmp(
  input: DmpInput,
  template: DmpTemplateId,
  p: DmpPhrases
): DocModel {
  const sections: DocSection[] = DMP_TEMPLATES[template].map((spec) => ({
    heading: p.headings[spec.heading] ?? "",
    blocks: sectionBlocks(input, spec, p),
  }));
  return { title: p.docTitle, meta: metaLines(input, template, p), sections };
}

export interface DmpSectionCheck {
  heading: string;
  status: "covered" | "missing";
  /** Required answers still empty in this section. */
  missing: DmpTextField[];
}

/** For each heading of the template: which required answers are still empty. */
export function checkDmp(
  input: DmpInput,
  template: DmpTemplateId
): DmpSectionCheck[] {
  return DMP_TEMPLATES[template].map((spec) => {
    const missing = spec.parts
      .flatMap((part) => part.fields)
      .filter(([field, optional]) => !(optional || isFilled(input, field)))
      .map(([field]) => field as DmpTextField);
    return {
      heading: spec.heading,
      status: missing.length === 0 ? "covered" : "missing",
      missing,
    };
  });
}

/** The answers a template never places — shown so nothing typed silently disappears. */
export function unusedFields(template: DmpTemplateId): DmpTextField[] {
  const used = new Set<DmpField>(
    DMP_TEMPLATES[template].flatMap((spec) =>
      spec.parts.flatMap((part) => part.fields.map(([field]) => field))
    )
  );
  return (Object.keys(EMPTY_DMP_INPUT) as (keyof DmpInput)[]).filter(
    (key): key is DmpTextField =>
      typeof EMPTY_DMP_INPUT[key] === "string" &&
      !["title", "pi", "funder", "grant", "date"].includes(key) &&
      !used.has(key as DmpField)
  );
}
